import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { papeleraAPI } from '../services/api';

const EXTENSIONES_INLINE = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'txt', 'html', 'htm'];

const esVisualizable = (nombre) => {
  const ext = nombre.split('.').pop()?.toLowerCase();
  return EXTENSIONES_INLINE.includes(ext);
};

const formatFecha = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('es-PE', {
    dateStyle: 'short',
    timeStyle: 'short'
  });
};

const PapeleraArchivos = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [retencionDias, setRetencionDias] = useState(7);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [restaurandoId, setRestaurandoId] = useState(null);
  const [viendoId, setViendoId] = useState(null);
  const [mensaje, setMensaje] = useState(null);

  const puedeGestionar = user && ['JEFE', 'ADMIN'].includes(user.rol);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await papeleraAPI.listar();
      setItems(res.data.items || []);
      setRetencionDias(res.data.retencionDias ?? 7);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'No se pudo cargar la papelera');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (puedeGestionar) cargar();
    else setLoading(false);
  }, [puedeGestionar, cargar]);

  const verArchivo = async (item) => {
    setViendoId(item.id);
    setMensaje(null);
    try {
      const forzarDescarga = item.esCarpeta || !esVisualizable(item.nombreArchivo);
      const res = await papeleraAPI.obtenerArchivo(item.id, forzarDescarga ? { download: '1' } : {});
      const blob = res.data;
      const contentType = res.headers['content-type'] || 'application/octet-stream';
      const url = window.URL.createObjectURL(new Blob([blob], { type: contentType }));

      if (!forzarDescarga) {
        window.open(url, '_blank', 'noopener,noreferrer');
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      } else {
        const link = document.createElement('a');
        link.href = url;
        link.download = item.esCarpeta ? `${item.nombreArchivo}.zip` : item.nombreArchivo;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      let texto = 'No se pudo abrir el archivo';
      if (err.response?.data instanceof Blob) {
        try {
          const parsed = JSON.parse(await err.response.data.text());
          texto = parsed.error || texto;
        } catch { /* ignore */ }
      } else if (err.response?.data?.error) {
        texto = err.response.data.error;
      }
      setMensaje({ tipo: 'error', texto });
    } finally {
      setViendoId(null);
    }
  };

  const restaurar = async (id) => {
    if (!window.confirm('¿Restaurar este elemento a su ubicación original en el expediente?')) return;
    setRestaurandoId(id);
    setMensaje(null);
    try {
      await papeleraAPI.restaurar(id);
      setMensaje({ tipo: 'ok', texto: 'Restaurado correctamente' });
      await cargar();
    } catch (err) {
      setMensaje({
        tipo: 'error',
        texto: err.response?.data?.error || 'Error al restaurar'
      });
    } finally {
      setRestaurandoId(null);
    }
  };

  if (!puedeGestionar) {
    return (
      <div className="container py-4">
        <div className="alert alert-warning">Solo usuarios JEFE o ADMIN pueden acceder a la papelera.</div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4 papelera-page">
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-2">
        <div>
          <h2 className="mb-1">Papelera de archivos</h2>
          <p className="text-muted mb-0">
            Elementos eliminados en los últimos {retencionDias} días. Pasado ese plazo se borran del servidor.
          </p>
        </div>
        <button type="button" className="btn btn-outline-primary" onClick={cargar} disabled={loading}>
          Actualizar
        </button>
      </div>

      {mensaje && (
        <div className={`alert alert-${mensaje.tipo === 'ok' ? 'success' : 'danger'}`} role="alert">
          {mensaje.texto}
        </div>
      )}

      {error && <div className="alert alert-danger">{error}</div>}

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status" />
        </div>
      ) : items.length === 0 ? (
        <div className="alert alert-info">No hay archivos ni carpetas en la papelera de los últimos {retencionDias} días.</div>
      ) : (
        <div className="table-responsive shadow-sm rounded bg-white">
          <table className="table table-hover table-sm mb-0 papelera-table">
            <thead className="thead-light">
              <tr>
                <th>Tipo</th>
                <th>Nombre</th>
                <th>Expediente</th>
                <th>Cliente</th>
                <th>Ruta original</th>
                <th>Eliminado por</th>
                <th>Fecha / hora</th>
                <th className="text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>{item.esCarpeta ? '📁 Carpeta' : '📄 Archivo'}</td>
                  <td className="font-weight-medium">{item.nombreArchivo}</td>
                  <td>
                    <a href={`/expedientes/${item.expedienteId}`} target="_blank" rel="noopener noreferrer">
                      {item.expedienteNumero}
                    </a>
                  </td>
                  <td>{item.clienteNombre}</td>
                  <td><small className="text-muted">{item.rutaOriginal}</small></td>
                  <td>{item.usuario?.nombre || '—'}</td>
                  <td>{formatFecha(item.fechaEliminacion)}</td>
                  <td className="text-center papelera-acciones">
                    <button
                      type="button"
                      className="papelera-btn papelera-btn-ver"
                      disabled={viendoId === item.id || restaurandoId === item.id}
                      onClick={() => verArchivo(item)}
                      title={item.esCarpeta ? 'Descargar carpeta como ZIP' : 'Ver o descargar archivo'}
                    >
                      {viendoId === item.id ? '...' : (item.esCarpeta ? 'Descargar ZIP' : 'Ver archivo')}
                    </button>
                    <button
                      type="button"
                      className="papelera-btn papelera-btn-restaurar"
                      disabled={restaurandoId === item.id || viendoId === item.id}
                      onClick={() => restaurar(item.id)}
                    >
                      {restaurandoId === item.id ? '...' : 'Restaurar'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <style>{`
        .papelera-page { max-width: 1400px; margin: 0 auto; }
        .papelera-table th { white-space: nowrap; font-size: 0.85rem; }
        .papelera-table td { vertical-align: middle; font-size: 0.9rem; }
        .papelera-acciones { white-space: nowrap; }
        .papelera-acciones .papelera-btn {
          display: inline-block;
          padding: 6px 14px;
          margin: 0 4px 0 0;
          font-size: 0.8125rem;
          font-weight: 600;
          line-height: 1.25;
          border-radius: 6px;
          border: 1px solid transparent;
          cursor: pointer;
          transition: background 0.15s ease, box-shadow 0.15s ease, transform 0.1s ease;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
        }
        .papelera-acciones .papelera-btn:last-child { margin-right: 0; }
        .papelera-acciones .papelera-btn-ver {
          background: #2563eb;
          border-color: #1d4ed8;
          color: #fff;
        }
        .papelera-acciones .papelera-btn-ver:hover:not(:disabled) {
          background: #1d4ed8;
          box-shadow: 0 2px 6px rgba(37, 99, 235, 0.35);
        }
        .papelera-acciones .papelera-btn-restaurar {
          background: #16a34a;
          border-color: #15803d;
          color: #fff;
        }
        .papelera-acciones .papelera-btn-restaurar:hover:not(:disabled) {
          background: #15803d;
          box-shadow: 0 2px 6px rgba(22, 163, 74, 0.35);
        }
        .papelera-acciones .papelera-btn:disabled {
          opacity: 0.55;
          cursor: not-allowed;
          box-shadow: none;
        }
      `}</style>
    </div>
  );
};

export default PapeleraArchivos;
